import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TeamAccessService } from '../services/team-access.service';
import { AuthService } from '../services/auth.service';
import { isSeatLimitError, shouldSuppressGenericError } from '../utils/http-error.utils';

const MAX_BCRYPT_PASSWORD_BYTES = 72;

@Component({
  selector: 'app-accept-invite',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
  ],
  templateUrl: './accept-invite.component.html',
  styleUrl: './accept-invite.component.less'
})
export class AcceptInviteComponent implements OnInit {
  loading = false;
  token = '';
  showPassword = false;
  showConfirmPassword = false;
  passwordStrength: 'weak' | 'medium' | 'strong' | '' = '';
  form: ReturnType<FormBuilder['group']>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private teamAccessService: TeamAccessService,
    private message: NzMessageService,
    private translate: TranslateService
  ) {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    this.form = this.fb.group({
      token: [this.token, [Validators.required]],
      password: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator,
        this.passwordByteLengthValidator,
      ]],
      confirmPassword: ['', [Validators.required]],
    }, {
      validators: [this.passwordMatchValidator]
    });
  }

  ngOnInit(): void {
    this.form.get('password')?.valueChanges.subscribe((value) => {
      this.passwordStrength = this.evaluatePasswordStrength(value || '');
      this.clearPasswordPolicyError();
    });

    if (this.token && this.authService.isAuthenticated()) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('appMode');
      sessionStorage.removeItem('authToken');
      this.translate.get('INVITE_ACCEPT.SWITCHING_ACCOUNT_INFO').subscribe((label) => {
        this.message.info(label);
      });
    }
  }

  evaluatePasswordStrength(password: string): 'weak' | 'medium' | 'strong' | '' {
    if (!password) return '';
    if (new TextEncoder().encode(password).length > MAX_BCRYPT_PASSWORD_BYTES) return '';

    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[\W_]/.test(password);
    const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;

    if (password.length >= 8 && score >= 4) return 'strong';
    if (password.length >= 6 && score >= 2) return 'medium';
    return 'weak';
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const password = this.form.controls['password'].value || '';
    const confirmPassword = this.form.controls['confirmPassword'].value || '';
    if (password !== confirmPassword) {
      this.message.error(this.translate.instant('INVITE_ACCEPT.PASSWORD_MISMATCH'));
      return;
    }

    this.loading = true;
    this.teamAccessService.acceptInvite({
      token: this.token,
      password,
    }).subscribe({
      next: (response) => {
        this.loading = false;
        const token = response?.token;
        if (!token) {
          this.message.error(this.translate.instant('ERRORS.INVITE_INVALID_OR_EXPIRED'));
          return;
        }

        this.authService.storeSession(token);
        this.message.success(this.translate.instant('INVITE_ACCEPT.SUCCESS_REDIRECT_DESKTOP'));
        if (this.isMobile()) {
          this.router.navigate(['/mobile-desktop']);
          return;
        }

        const decodedToken = this.authService.decodeToken(token);
        const userRole = decodedToken?.role || '';
        if (userRole === 'Admin') {
          this.router.navigate(['/admin/dashboard']);
        } else {
          this.router.navigate(['/dashboard']);
        }
      },
      error: (error) => {
        this.loading = false;
        const errorCode = error?.error?.code || error?.code;
        if (errorCode === 'PASSWORD_INVALID' || errorCode === 'PASSWORD_TOO_LONG') {
          const passwordControl = this.form.get('password');
          if (errorCode === 'PASSWORD_INVALID') {
            passwordControl?.setErrors({
              ...(passwordControl.errors || {}),
              passwordPolicyInvalid: true,
            });
          }
          passwordControl?.markAsTouched();
          this.message.error(this.translate.instant(
            errorCode === 'PASSWORD_TOO_LONG' ? 'SIGNUP.PASSWORD_TOO_LONG' : 'SIGNUP.PASSWORD_COMPLEXITY'
          ));
          return;
        }
        if (shouldSuppressGenericError(error) || isSeatLimitError(error)) {
          return;
        }
        this.message.error(this.translate.instant('ERRORS.INVITE_INVALID_OR_EXPIRED'));
      }
    });
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  private passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = (control.value || '').toString();
    if (!value) {
      return null;
    }

    const hasLower = /[a-z]/.test(value);
    const hasUpper = /[A-Z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecial = /[\W_]/.test(value);

    return hasLower && hasUpper && hasNumber && hasSpecial ? null : { passwordStrength: true };
  }

  private passwordByteLengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = typeof control.value === 'string' ? control.value : '';
    return new TextEncoder().encode(password).length > MAX_BCRYPT_PASSWORD_BYTES
      ? { passwordTooLong: true }
      : null;
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    if (!password || !confirmPassword) {
      return null;
    }
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  private clearPasswordPolicyError(): void {
    const passwordControl = this.form.get('password');
    const errors = passwordControl?.errors;
    if (!passwordControl || !errors?.['passwordPolicyInvalid']) {
      return;
    }

    const remainingErrors = { ...errors };
    delete remainingErrors['passwordPolicyInvalid'];
    passwordControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }
}
